/**
 * SessionWarningModal
 *
 * Non-dismissible security warning shown before HIPAA inactivity logout.
 * Uses Radix Dialog primitives directly so we can block ESC and outside-click
 * dismissal — the user must actively choose to stay or leave.
 *
 * @module @ayla/ui
 */

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../lib/utils";
import { Button } from "./Button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionWarningModalProps {
  /** Controls modal visibility. */
  isOpen: boolean;
  /**
   * Milliseconds remaining until auto-logout, used to seed the countdown
   * display when the modal opens. The component manages the ticking internally.
   */
  timeRemainingMs: number;
  /** Called when the user clicks "Stay Logged In". Should call extendSession(). */
  onStayLoggedIn: () => void;
  /** Called when the user clicks "Logout Now". Should call the logout handler. */
  onLogoutNow: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats milliseconds into a `m:ss` countdown string (e.g. "4:58", "0:05").
 * Never returns negative values.
 */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SessionWarningModal({
  isOpen,
  timeRemainingMs,
  onStayLoggedIn,
  onLogoutNow,
}: SessionWarningModalProps) {
  const [displayMs, setDisplayMs] = React.useState(timeRemainingMs);

  // Re-seed the countdown display whenever the modal opens or the seed changes.
  React.useEffect(() => {
    if (isOpen) {
      setDisplayMs(timeRemainingMs);
    }
  }, [isOpen, timeRemainingMs]);

  // Tick the countdown while the modal is open. Clears automatically on close.
  React.useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setDisplayMs(prev => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const countdownLabel = formatCountdown(displayMs);

  return (
    <DialogPrimitive.Root open={isOpen}>
      <DialogPrimitive.Portal>
        {/* Overlay — slightly darker than the shared Dialog to convey urgency */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          )}
        />

        <DialogPrimitive.Content
          // Block all dismiss paths — user must explicitly choose an action.
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2",
            "rounded-lg border bg-background p-6 shadow-xl",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "focus:outline-none"
          )}
          // Prevent Radix from automatically focusing the first element.
          // We manually apply `autoFocus` to the "Stay Logged In" button instead.
          onOpenAutoFocus={e => e.preventDefault()}
        >
          {/* Header */}
          <div className="mb-4 flex flex-col gap-1.5">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight text-foreground">
              Session Expiring Soon
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              You will be automatically logged out due to inactivity.
            </DialogPrimitive.Description>
          </div>

          {/* Countdown */}
          <div
            className="mb-6 flex flex-col items-center gap-1 rounded-md bg-amber-50 py-5 dark:bg-amber-950/30"
            role="timer"
            aria-live="polite"
            aria-atomic="true"
            aria-label={`Time until automatic logout: ${countdownLabel}`}
          >
            <span className="text-4xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
              {countdownLabel}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-amber-600/70 dark:text-amber-400/70">
              until logout
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={onLogoutNow}
              className="w-full sm:w-auto"
            >
              Logout Now
            </Button>
            <Button
              // autoFocus so keyboard users land here first — the safer default action
              autoFocus
              onClick={onStayLoggedIn}
              className="w-full sm:w-auto"
            >
              Stay Logged In
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
